import { DEFAULT_PAGE_SIZE } from '@/constants';
import { CouponTemplateType } from '@/constants/coupon';
import { EnableStatus, ListResponse } from '@/types/common';
import {
  CouponInfo,
  CouponIssueFailDetail,
  CouponIssueRecord,
  VoidCouponIssueParams,
  VoidCouponIssueResult,
} from '@/types/coupon';
import { cleanObject } from '@/utils';
import { requestWrapper } from '@/utils/request';

// 获取优惠券列表
export async function getCouponList(
  params: {
    page?: number;
    page_size?: number;
    [key: string]: any;
    type?: CouponTemplateType; // 优惠券类型
    status?: EnableStatus; // 状态
    with_used_count?: boolean;
    hidden_target_new_user?: boolean; // 隐藏新用户专享券
  } = {},
) {
  const defaultParams = {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    type: CouponTemplateType.LessonCoupon, // 默认设置为1
    status: EnableStatus.Enabled, // 默认设置为生效中
  };

  const mergedParams = {
    ...defaultParams,
    ...params,
  };
  return requestWrapper<ListResponse<CouponInfo>>('/api/coupon_template/list', {
    method: 'GET',
    params: mergedParams,
  });
}

// 创建优惠券
export async function createCoupon(params: Partial<CouponInfo>) {
  const mergedParams = {
    ...params,
    course_type: typeof params.course_type === 'number' ? params.course_type : -1, // 服务端这个字段设置成了必填，没设置的默认设置为-1
  };
  return requestWrapper<void>('/api/coupon_template/create', {
    method: 'POST',
    data: mergedParams,
  });
}

// 更新优惠券
export async function updateCoupon(params: Partial<CouponInfo>) {
  const mergedParams = {
    ...params,
    course_type: typeof params.course_type === 'number' ? params.course_type : -1, // 服务端这个字段设置成了必填，没设置的默认设置为-1
  };
  return requestWrapper<void>('/api/coupon_template/update', {
    method: 'POST',
    data: mergedParams,
  });
}

// 激活优惠券
export async function activateCoupon(params: { issue_id: number }) {
  return requestWrapper<void>('/api/coupon_issue/activate', {
    method: 'POST',
    data: params,
  });
}

// 获取优惠券详情
export async function geCouponInfo(params: Partial<CouponInfo>) {
  return requestWrapper<CouponInfo>('/api/coupon_template/info', {
    method: 'GET',
    params,
  });
}

// 获取优惠券发放记录
export async function getCouponIssueRecordList(params: {
  template_type?: CouponTemplateType; // 优惠券类型
  template_id?: number;
  user_id?: number;
  is_active?: number; // 是否有效 1:有效 2:无效
  page?: number;
  page_size?: number;
  issue_status?: number;

  issue_admin_id?: number;
  issue_time_start?: string;
  issue_time_end?: string;
  include_void?: EnableStatus; // 是否作废
}) {
  const defaultParams = {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
  };

  const mergedParams = {
    ...defaultParams,
    ...params,
  };

  return requestWrapper<ListResponse<CouponIssueRecord>>('/api/coupon_issue/list', {
    method: 'GET',
    params: mergedParams,
  });
}

// 发放优惠券
export async function issueCoupon(params: {
  template_id: number; // 模板 ID
  phone_numbers?: string[]; // 手机号列表
  per_coupon_class_num?: number; // 课次数
  validity_end: string; // 有效期结束时间
  user_ids?: number[]; // 用户 ID 列表 用于当手机号不存在时传递id列表
  remark?: string; // 发券备注
  course_type?: number;
}) {
  return requestWrapper<CouponIssueFailDetail>('/api/coupon_issue/create', {
    method: 'POST',
    data: cleanObject(params),
  });
}

// 更新发放的优惠券
export async function updateCouponIssue(params: Partial<CouponInfo>) {
  return requestWrapper<void>('/api/coupon_issue/update', {
    method: 'POST',
    data: params,
  });
}

// 作废发放的优惠券
export async function voidCouponIssue(params: VoidCouponIssueParams) {
  return requestWrapper<VoidCouponIssueResult>('/api/coupon_issue/void', {
    method: 'POST',
    data: params,
  });
}

// 变更代金券失效日期
export async function updateCouponIssueValidityEnd(params: { id: number; validity_end: string }) {
  return requestWrapper<void>('/api/coupon_issue/validity_end/update', {
    method: 'POST',
    data: params,
  });
}

// 导出优惠券发放记录
export async function exportCouponIssueRecord(params: Record<string, unknown> = {}) {
  return requestWrapper<{ url: string }>('/api/coupon_issue/export', {
    method: 'GET',
    params,
  });
}
